import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDownIcon } from '../icons';
import { cn } from '../../utils/cn';

export interface CustomDropdownOption {
  value: string;
  label: string;
  description?: string;
  group?: string;
}

interface CustomDropdownProps {
  label?: string;
  value?: string | null;
  placeholder?: string;
  options: CustomDropdownOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  dropdownWidthClassName?: string;
  emptyStateText?: string;
  compact?: boolean;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  label,
  value = '',
  placeholder = 'Select…',
  options,
  onChange,
  disabled = false,
  className,
  buttonClassName,
  dropdownWidthClassName = 'w-full',
  emptyStateText = 'No options',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const selectedOption = options.find(option => option.value === value);

  const updateMenuPosition = () => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
      zIndex: 1000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value, options.length]);

  useEffect(() => {
    if (!open) return;

    const handleReposition = () => updateMenuPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetNode = event.target as Node;
      const clickedInsideContainer = !!containerRef.current?.contains(targetNode);
      const clickedInsideMenu = !!menuRef.current?.contains(targetNode);
      if (!clickedInsideContainer && !clickedInsideMenu) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleMenu = () => {
    if (disabled) return;
    setOpen(prev => !prev);
  };

  const handleSelect = (selectedValue: string) => {
    onChange(selectedValue);
    setOpen(false);
  };

  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(() => {
    // If a value is selected and it belongs to a group, pre-expand that group
    if (selectedOption?.group) {
      return { [selectedOption.group]: true };
    }
    return {};
  });

  const toggleGroup = (group: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Group options logically
  const renderOptions = () => {
    const groups: { name: string; options: CustomDropdownOption[] }[] = [];
    const ungrouped: CustomDropdownOption[] = [];

    options.forEach(opt => {
      if (opt.group) {
        let g = groups.find(g => g.name === opt.group);
        if (!g) {
          g = { name: opt.group, options: [] };
          groups.push(g);
        }
        g.options.push(opt);
      } else {
        ungrouped.push(opt);
      }
    });

    const elements: React.ReactNode[] = [];

    // Render grouped options
    groups.forEach((g, index) => {
      const isExpanded = !!expandedGroups[g.name];
      elements.push(
        <div 
          key={`group-${g.name}`}
          onClick={(e) => toggleGroup(g.name, e)}
          className={cn(
            "px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 bg-slate-50 border border-slate-200 dark:border-white/10 dark:text-slate-300 dark:bg-[#121726] flex justify-between items-center sticky top-0 backdrop-blur-md z-10 shadow-sm rounded-md cursor-pointer transition-colors hover:bg-slate-100 dark:hover:bg-[#161B2E]",
            index !== 0 && "mt-1.5",
            isExpanded && "mb-0 rounded-b-none border-b-0 shadow-none sticky -top-1" // sticky slightly off so it locks beautifully
          )}
        >
          <span>{g.name}</span>
          <div className={cn("p-0.5 rounded-full bg-slate-200/50 dark:bg-slate-700 flex items-center justify-center transition-transform duration-200", isExpanded && "rotate-180")}>
            <ChevronDownIcon className="w-3 h-3 text-slate-500 dark:text-slate-400" />
          </div>
        </div>
      );

      if (isExpanded) {
        g.options.forEach((option, idx) => {
          const isActive = option.value === value;
          const isLast = idx === g.options.length - 1;
          elements.push(
            <button
              key={option.value}
              type="button"
              onClick={() => handleSelect(option.value)}
              className={cn(
                'w-full text-left px-4 py-2 text-sm font-medium transition-colors border-l border-r border-b border-slate-100 dark:border-slate-800/80 bg-white dark:bg-transparent',
                'hover:bg-slate-50/80 dark:hover:bg-white/10',
                isActive
                  ? 'bg-orange-50 text-orange-600 font-semibold dark:bg-orange-600/10 dark:text-orange-400'
                  : 'text-slate-600 dark:text-slate-300',
                isLast && 'rounded-b-md mb-1'
              )}
            >
              <div className="flex flex-col">
                <span>{option.label}</span>
                {option.description && (
                  <span className="text-xs text-slate-500 dark:text-slate-400">
                    {option.description}
                  </span>
                )}
              </div>
            </button>
          );
        });
      }
    });

    // Render ungrouped options
    ungrouped.forEach(option => {
      const isActive = option.value === value;
      elements.push(
        <button
          key={option.value}
          type="button"
          onClick={() => handleSelect(option.value)}
          className={cn(
            'w-full text-left px-3 py-2 text-sm font-medium transition-colors rounded-md mb-1 mt-1',
            'hover:bg-slate-100/80 dark:hover:bg-white/10',
            isActive
              ? 'bg-orange-50 text-orange-600 font-semibold dark:bg-orange-500/10 dark:text-orange-400'
              : 'text-slate-600 dark:text-zinc-200'
          )}
        >
          <div className="flex flex-col">
            <span>{option.label}</span>
            {option.description && (
              <span className="text-xs text-slate-500 dark:text-slate-400">
                {option.description}
              </span>
            )}
          </div>
        </button>
      );
    });

    return elements;
  };

  return (
    <div className={cn('relative space-y-1', open && 'z-50', className)} ref={containerRef}>
      {label && (
        <label className={cn(
          "block text-xs font-semibold tracking-wide text-slate-600 dark:text-zinc-300",
          compact ? "mb-0.5" : "mb-1"
        )}>
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={toggleMenu}
        disabled={disabled}
        ref={buttonRef}
        className={cn(
          'w-full px-3 rounded-md border text-left flex items-center justify-between gap-2 text-sm font-semibold tracking-wide transition-all min-h-[38px] sm:min-h-[28px]',
          compact ? 'h-8 sm:h-7 py-1 sm:py-0' : 'py-2.5 sm:py-2',
          'bg-white text-slate-800 border-slate-200 shadow-sm hover:bg-slate-50',
          'dark:bg-[#26344d] dark:text-slate-50 dark:border-slate-400/40 dark:hover:bg-[#2b3a55] dark:hover:border-orange-400/60',
          !selectedOption && 'text-slate-500 dark:text-slate-300',
          disabled && 'opacity-60 cursor-not-allowed',
          buttonClassName
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 transition-transform',
            open ? 'rotate-180' : 'rotate-0',
            disabled ? 'text-slate-400 dark:text-slate-500' : 'text-slate-500 dark:text-slate-300'
          )}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={cn(
              'rounded-lg border bg-white border-slate-200 shadow-2xl max-h-[70vh] overflow-y-auto flex flex-col',
              'dark:bg-[#202c42] dark:border-slate-400/30 dark:shadow-[0_20px_60px_rgba(2,6,23,0.58)]',
              dropdownWidthClassName
            )}
            style={menuStyle}
            role="listbox"
          >
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-300">
                {emptyStateText}
              </div>
            )}

            <div className="flex flex-col p-1 gap-0.5">
              {renderOptions()}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default CustomDropdown;
